import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import TabNavigation from '../../components/TabNavigation';
import { MapPin, Plus, FileText, X, Building2, Database } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAlert } from '../../context/AlertContext';
import { usePermissions } from '../../context/PermissionContext';
import { useSite } from '../../context/SiteContext';

const MASTER_DATA_ROLES = ['admin', 'hse manager', 'safety manager', 'hse_manager', 'safety_manager', 'tenant_admin', 'super_admin'];

const SitesList = ({ user, onLogout }) => {
  const { showError } = useAlert();
  const navigate = useNavigate();
  const { hasPermission, isAdmin, hasRole } = usePermissions();
  const { activeSiteId, availableSites, isCorpUser } = useSite();
  const canManageMasterData = isAdmin() || MASTER_DATA_ROLES.some(r => hasRole(r));
  const [searchParams] = useSearchParams();
  const companyId = searchParams.get('company_id');

  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    country: 'India',
    company_id: user?.company_id || ''
  });

  useEffect(() => {
    fetchSites();
  }, [activeSiteId]);

  const fetchSites = async () => {
    try {
      const token = localStorage.getItem('token');
      // Use shared org/sites endpoint (aligned with org_nodes)
      const url = activeSiteId
        ? `${API}/org/sites?site_id=${activeSiteId}`
        : `${API}/org/sites`;
      const sitesRes = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Normalize response: org/sites returns objects with site_id/site_name,
      // map to id/name for backward compatibility with card rendering
      const normalized = (sitesRes.data || []).map(s => ({
        id: s.site_id || s.id,
        name: s.site_name || s.name,
        location: s.location || s.address || '',
        country: s.country || '',
        company_name: s.company_name || ''
      }));
      setSites(normalized);
    } catch (error) {
      console.error('Failed to fetch sites:', error);
      // Fallback to legacy ERS sites endpoint
      try {
        const token = localStorage.getItem('token');
        const fallbackRes = await axios.get(`${API}/ers/sites`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSites(fallbackRes.data);
      } catch (fallbackError) {
        console.error('Fallback fetch also failed:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/ers/sites`, {
        ...formData,
        company_id: user?.company_id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowAddModal(false);
      setFormData({ name: '', location: '', country: 'India', company_id: user?.company_id || '' });
      fetchSites();
    } catch (error) {
      console.error('Failed to create site:', error);
      showError('Failed to create site: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Sites</h1>
              <p className="text-gray-600 mt-2">Manage facility locations</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/energy-resources')}
                className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg font-medium transition-colors"
              >
                ← Back to ERS Dashboard
              </button>
            </div>
          </div>
        </div>

        {/* Sites Grid */}
        {sites.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <MapPin className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No sites yet</h3>
            <p className="text-gray-600">Sites are managed from the Admin module. Contact your administrator to add sites.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sites.map((site) => (
              <div
                key={site.id}
                onClick={() => navigate(`/ers/studies?site_id=${site.id}`)}
                className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-green-500"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="bg-green-100 p-3 rounded-lg">
                    <MapPin className="w-6 h-6 text-green-600" />
                  </div>
                  <span className="text-xs font-semibold px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                    {site.country}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{site.name}</h3>
                {site.company_name && (
                  <div className="flex items-center text-sm text-gray-600 mb-2">
                    <Building2 className="w-4 h-4 mr-1" />
                    {site.company_name}
                  </div>
                )}
                <p className="text-sm text-gray-600 mb-4">{site.location}</p>
                <div className="flex items-center gap-2">
                  {canManageMasterData && (
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/ers/site/${site.id}/master-dataset`); }}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
                    >
                      <Database className="w-3.5 h-3.5" />
                      Manage Dataset
                    </button>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/ers/studies?site_id=${site.id}`); }}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    <FileText className="w-3.5 h-3.5" />
                    View Studies
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add Site Modal */}
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add New Site</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-900">
                  <strong>Company:</strong> {user?.company_name || 'Your Company'}
                </p>
                <p className="text-xs text-blue-700 mt-1">Site will be created for your company</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Site Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="e.g., Mumbai Plant"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="e.g., Thane, Maharashtra"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country *</label>
                <select
                  value={formData.country}
                  onChange={(e) => setFormData({...formData, country: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                >
                  <option value="India">India</option>
                  <option value="Singapore">Singapore</option>
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
                >
                  Add Site
                </button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default SitesList;